/**
 * React hook for speech functionality
 * 
 * Provides easy access to speech-to-text and text-to-speech features
 */

import { useCallback, useEffect, useState } from 'react';
import { speechService } from '../services/speechService';

export const useSpeech = () => {
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supported, setSupportedFeatures] = useState({ stt: false, tts: false });

  useEffect(() => {
    // Check support on mount
    setSupportedFeatures(speechService.isSupported());
  }, []);

  /**
   * Start listening for speech input
   */
  const startListening = useCallback((onResult: (text: string) => void, language?: string) => {
    setError(null);
    setIsListening(true);

    speechService.startListening(
      (text) => {
        setIsListening(false);
        onResult(text);
      },
      (errorMessage) => {
        setIsListening(false);
        setError(errorMessage);
      },
      language
    );
  }, []);

  /**
   * Stop listening
   */
  const stopListening = useCallback(() => {
    speechService.stopListening();
    setIsListening(false);
  }, []);

  /**
   * Speak text aloud
   */
  const speak = useCallback((text: string, onEnd?: () => void, userGender?: 'male' | 'female', userLanguage?: string) => {
    setError(null);
    setIsSpeaking(true);

    speechService.speak(text, () => {
      setIsSpeaking(false);
      if (onEnd) {
        onEnd();
      }
    }, userGender, userLanguage);
  }, []);

  /**
   * Stop speaking
   */
  const stopSpeaking = useCallback(() => {
    speechService.stopSpeaking();
    setIsSpeaking(false);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    startListening,
    stopListening,
    speak,
    stopSpeaking,
    isListening,
    isSpeaking,
    error,
    clearError,
    supported,
  };
};
